import Cookie from 'js-cookie'
import cookies from 'next-cookies'
import { NextPageContext } from 'next'
import { FooterColumn } from '../types/core'

export enum Cookies {
  // `gateway` is used as a flag to disable ads for users that have been flagged via
  // the "ad free experience" gateway that some institutions have an agreement with Chegg to use
  Gateway = 'gateway',
  // `al_cell` is used to determine the ad layout user segmentation for some sites
  AdLayout = 'al_cell',
  // `V` is a cookie containing the unique id used to bucket users into optimizely experiments
  ExperimentIdentifier = 'V',
  // `tpudsa` is a cookie set for a premium user. It's an acronym for "they paid us, don't show ads".
  HideAdsPaidUser = 'tpudsa',
  // `userRole` is used to save the role of a user on the site
  UserRole = 'userRole',
  // `optUserProfile` is the user profile cookie for optimizely
  OptimizelyUserProfile = 'opt-user-profile',
  // `devoptUserProfile` is the user profile cookie for optimizely used in a dev environment
  DevOptimizelyUserProfile = 'devopt-user-profile',
  // `id_token` is the authentication id token of the user
  IdToken = 'id_token',
  // `exp` is a cookie used to track client experiments used when initializizing rio
  RioClientExperiments = `exp`,
  // `SU` is used to provide a session user id to the jericho service
  JerichoSession = `SU`,
  // `opt-is-sem` is used to denote that a user entered the app via search engine marketing
  OptimizelyFromMarketing = `opt-is-sem`,
  // `sbm_previous_truex_gcs` is set to 'true' by sbm-unlock after the user has viewed unlock, with a 48 hour expiration
  PreviousTruex = `sbm_previous_truex_gcs`,
  // `viewed_ad_unlock` is set to 'true' after the user has viewed ad unlock variant1/variant2, with a 12 hour expiration
  ViewedAdUnlock = `viewed_ad_unlock`,
  // `paper_uploaded` is used to track across applications whether a user has uploaded a paper through the wtai-upload-widget
  PaperUploaded = `paper_uploaded`,
  // `optimizely_test_cookie` is set to 'true' when a QA user adds a 'test-audience' query string key
  OptimizelyTestCookie = `optimizely_test_cookie`,
  CwcRollout = 'cwc-rollout',
  // `sbm_viewed_copy_interstitial` is set to 'true' by sbm after the user has viewed interstitial ad, with a 24 hour expiration
  SbmViewedCopyInterstitial = `sbm_viewed_copy_interstitial`,
  // temp cookie to test oneAuth changes
  OneAuth = `one_auth`,
  // CHEGGUSERACCESSTOKEN is set by CFE through token call after OneAuth integration. We remove this when user logs out.
  CheggUserAccessToken = 'CHEGGUSERACCESSTOKEN',
  // (Pricing Experiment) Discount modal shown to user?
  DiscountModalShown = 'discountModalShown',
  // llm model
  model = 'model',
  access_token='access_token'

  // writing assistant Sprig survey cookie, 'waFeebdackSurveyStatus' =  true means survey has completed, will have a expiry of 180 days.
  // waFeebdackSurveyStatus = 'waFeebdackSurveyStatus',
}

// optional configuration values for domain and expiration
type CookieConfig = {
  domain?: string
  expires?: number | Date
  secure?: boolean
}

// set a cookie using one of the Cookies enum values
export const setCookie = (cookie: Cookies, value, config?: CookieConfig) => {
  // todo: figure out what to do if we try to set the invalid cookie
  const cookieConfig = { ...config, secure: true }
  return Cookie.set(cookie, value, cookieConfig)
}

// get a cookie using one of the Cookies enum values
export const getCookie = (cookie: Cookies) => Cookie.get(cookie)

// remove a cookie using one of the Cookies enum values
export const removeCookie = (cookie: Cookies) => Cookie.remove(cookie)

// reads contents of al_ff cookie, if present, to set feature flags on initial state
export const getBackendOpts = (ctx: NextPageContext) => {
  const alffMatch = cookies(ctx).al_ff
  if (alffMatch) {
    try {
      // Base64 conversion adds one or two `=` at the end of the resulting string when padding is needed.
      // This is encoded as `%3D` when it hits the user's cookies. Reverse this to ensure we can decode.
      const base64String = decodeURIComponent(alffMatch)
      // archie@edge encodes the feature flag object in base64.
      const jsonString = Buffer.from(base64String, 'base64').toString('ascii')
      return JSON.parse(jsonString)
    } catch (e) {
      const err = new Error(`Unable to parse al_ff cookie value: ${alffMatch}`)
      throw err
    }
  }
  return {}
}

const gdprCountryCodes = new Set([
  'AT',
  'BE',
  'BG',
  'CY',
  'CH',
  'CZ',
  'DE',
  'DK',
  'EE',
  'ES',
  'FI',
  'FR',
  'GB',
  'GR',
  'HR',
  'HU',
  'IE',
  'IS',
  'IT',
  'LI',
  'LT',
  'LU',
  'LV',
  'MT',
  'NL',
  'NO',
  'PL',
  'PT',
  'RO',
  'SE',
  'SI',
  'SK',
  'UK',
])

/**
 * add cookie links as per country & region
 * @param footerLinkColumns footer link list
 * @param country user country
 * @param countryRegion user country region
 */
export const addGDPRCookieLinks = (
  footerLinkColumns: FooterColumn[],
  country: string,
  countryRegion?: string
): FooterColumn[] => {
  if (gdprCountryCodes.has(country)) return footerLinkColumns

  const footerLinkCopy = <FooterColumn[]>JSON.parse(JSON.stringify(footerLinkColumns))
  const businessFooterLinks = footerLinkCopy.find((flc) => flc.title === 'Business')
  const linkName =
    country === 'US' && countryRegion === 'CA' ? 'DO NOT SELL MY INFO' : 'Cookie Settings'

  businessFooterLinks.links.push({
    name: linkName,
    id: 'ot-sdk-btn',
    className: 'ot-sdk-show-settings',
    url: '#',
  })
  return footerLinkCopy
}
